import { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Dialog } from "./Dialog";
import { cn } from "@/lib/utils";

export type FilterGroup = {
  id: string;
  label: string;
  value: string;
  /** The value that means "no filter": not counted or shown as a chip. */
  defaultValue: string;
  options: { value: string; label: string; count?: number }[];
  /** Left out of the chips and the count (e.g. sorting). */
  quiet?: boolean;
};

const chip = (on: boolean) =>
  cn(
    "rounded-full border border-[var(--border)] px-3 py-1 text-[13px]",
    on
      ? "border-[var(--primary)] bg-[var(--primary-soft)] text-[var(--primary)]"
      : "bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
  );

/**
 * A "Filters" button with the filters that are on as removable chips; every filter
 * lives in one dialog, so the page above the cards stays clean.
 */
export function FilterBar({
  title,
  groups,
  onChange,
  onClear,
  shown,
}: {
  title: string;
  groups: FilterGroup[];
  onChange: (id: string, value: string) => void;
  onClear: () => void;
  /** How many results the filters leave, for the dialog's button. */
  shown: number;
}) {
  const [open, setOpen] = useState(false);
  const active = groups.filter((g) => !g.quiet && g.value !== g.defaultValue);

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <button type="button" className="btn btn-soft" onClick={() => setOpen(true)}>
        <SlidersHorizontal size={14} aria-hidden />
        Filters
        {active.length > 0 && (
          <span className="num rounded-full bg-[var(--primary)] px-1.5 text-[11px] leading-[18px] text-white">{active.length}</span>
        )}
      </button>
      {active.map((g) => (
        <button
          key={g.id}
          type="button"
          aria-label={`Remove filter ${g.label}`}
          onClick={() => onChange(g.id, g.defaultValue)}
          className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--bg-card-elevated)] px-2.5 py-[3px] text-xs text-[var(--text-primary)] hover:border-[var(--border-hover)]"
        >
          {g.options.find((o) => o.value === g.value)?.label ?? g.value}
          <X size={12} aria-hidden />
        </button>
      ))}

      {open && (
        <Dialog title={title} onClose={() => setOpen(false)}>
          <div className="flex flex-col gap-5">
            {groups.map((g) => (
              <fieldset key={g.id} className="m-0 border-0 p-0">
                <legend className="mb-2 p-0 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">{g.label}</legend>
                <div className="flex flex-wrap gap-1.5">
                  {g.options.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={g.value === o.value}
                      onClick={() => onChange(g.id, o.value)}
                      className={chip(g.value === o.value)}
                    >
                      {o.label}
                      {o.count != null && <span className="num ml-1.5 text-xs opacity-70">{o.count}</span>}
                    </button>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between gap-2">
            <button type="button" className="btn btn-ghost btn-sm" disabled={!active.length} onClick={onClear}>
              Clear all
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(false)}>
              Show {shown.toLocaleString()} {shown === 1 ? "result" : "results"}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
